import { ButtonSecondary, Colors, Icon, ModalDialog, Text } from '@amelio/ui-library';
import { useEffect, useState } from 'react';

import ManagerStatusSection from './ManagerStatusSection';

import { useGetManagerLaunchStatusPaged } from '@/api/cycle-evaluation-services/manager-launch-status';
import managerLaunchStatusService from '@/api/cycle-evaluation-services/manager-launch-status/managerLaunchStatusService';
import { useAmelioTranslations } from '@/hooks';
import { useCompanyInformation } from '@/hooks/company/useCompanyInformation';
import { useError } from '@/hooks/utils/useError';
import { downloadFile } from '@/utils/fileDownload';

type Props = {
    isOpen: boolean;
    sentCycleId: string;
    onClose: () => void;
};

const PAGE_SIZE = 5;

export const ManagerLaunchStatusModal = ({ isOpen, sentCycleId, onClose }: Props) => {
    const { t } = useAmelioTranslations();
    const { getManagerLabelCapitalized } = useCompanyInformation();
    const { handleError } = useError();
    const [notLaunchedPage, setNotLaunchedPage] = useState(1);
    const [launchedPage, setLaunchedPage] = useState(1);
    const [isExporting, setIsExporting] = useState(false);

    const { data: notLaunchedData, isLoading: isNotLaunchedLoading } =
        useGetManagerLaunchStatusPaged({
            sentCycleId,
            isLaunched: false,
            pageNumber: notLaunchedPage,
            pageSize: PAGE_SIZE,
        });

    const { data: launchedData, isLoading: isLaunchedLoading } = useGetManagerLaunchStatusPaged({
        sentCycleId,
        isLaunched: true,
        pageNumber: launchedPage,
        pageSize: PAGE_SIZE,
    });

    const notLaunchedManagers = notLaunchedData?.items ?? [];
    const notLaunchedTotalCount = notLaunchedData?.totalCount ?? 0;
    const notLaunchedTotalPages = Math.ceil(notLaunchedTotalCount / PAGE_SIZE);

    const launchedManagers = launchedData?.items ?? [];
    const launchedTotalCount = launchedData?.totalCount ?? 0;
    const launchedTotalPages = Math.ceil(launchedTotalCount / PAGE_SIZE);

    // Calculate total managers from the actual API responses
    // Total = launched managers + not launched managers
    const totalManagers = launchedTotalCount + notLaunchedTotalCount;

    useEffect(() => {
        setNotLaunchedPage(1);
        setLaunchedPage(1);
    }, [sentCycleId]);

    const handleDownloadExcel = async () => {
        try {
            setIsExporting(true);
            const blob = await managerLaunchStatusService.exportManagerLaunchStatus({
                meta: { sentCycleId },
            });
            downloadFile(blob, `manager-launch-status-${sentCycleId}-${Date.now()}.xlsx`);
        } catch (error) {
            handleError({ error });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <ModalDialog
            isOpen={isOpen}
            onClose={onClose}
            title={t('cycleEvaluationDashboard.managerLaunchStatus.title')}
            titleIcon={
                <Icon
                    name={'business-chart'}
                    color={Colors.PRIMARY}
                    ariaHidden
                />
            }
            subtitle={t('cycleEvaluationDashboard.managerLaunchStatus.subtitle', {
                launched: launchedTotalCount,
                total: totalManagers,
            })}
            size={'lg'}
            closeAriaLabel={t('common.close')}
            backdropBlur
            dataTestId={'manager-launch-status-modal'}
            footer={
                <div
                    className={'flex items-center justify-between gap-4'}
                    data-testid={'manager-launch-status-footer'}
                >
                    <Text
                        size={'small'}
                        color={Colors.BASE_600}
                        className={'italic'}
                    >
                        {t('cycleEvaluationDashboard.managerLaunchStatus.footerNote')}
                    </Text>

                    <ButtonSecondary
                        size={'md'}
                        dataTestId={'download-excel-button'}
                        onClick={handleDownloadExcel}
                        disabled={isExporting}
                    >
                        <span className={'flex items-center gap-2'}>
                            <Icon
                                name={'arrow-arrow-down-1'}
                                size={16}
                            />

                            {t('cycleEvaluationDashboard.managerLaunchStatus.downloadExcel')}
                        </span>
                    </ButtonSecondary>
                </div>
            }
        >
            <div className={'flex flex-col gap-1'}>
                <div
                    className={'flex items-center gap-2 py-3'}
                    data-testid={'not-launched-header'}
                >
                    <Icon
                        name={'time-clock'}
                        color={Colors.ERROR}
                        size={15}
                    />

                    <Text
                        weight={'bold'}
                        size={'small'}
                    >
                        {t('cycleEvaluationDashboard.managerLaunchStatus.notLaunchedYet', {
                            count: notLaunchedTotalCount,
                        })}
                    </Text>
                </div>

                <div className={'border-base-200 overflow-hidden rounded-lg border'}>
                    <div
                        className={
                            'bg-base-50 border-base-200 grid grid-cols-[2fr_1fr] border-b px-4 py-3'
                        }
                        data-testid={'not-launched-table-header'}
                    >
                        <Text
                            weight={'bold'}
                            size={'small'}
                        >
                            {t('cycleEvaluationDashboard.managerLaunchStatus.columns.manager', {
                                managerLabel: getManagerLabelCapitalized(),
                            })}
                        </Text>

                        <Text
                            weight={'bold'}
                            size={'small'}
                            className={'text-right'}
                        >
                            {t(
                                'cycleEvaluationDashboard.managerLaunchStatus.columns.launchedRequired',
                            )}
                        </Text>
                    </div>

                    <div className={'px-2 py-2'}>
                        <ManagerStatusSection
                            isLaunched={false}
                            isLoading={isNotLaunchedLoading}
                            managers={notLaunchedManagers}
                            totalCount={notLaunchedTotalCount}
                            onPageChange={setNotLaunchedPage}
                            currentPage={notLaunchedPage}
                            totalPages={notLaunchedTotalPages}
                        />
                    </div>
                </div>

                <div
                    className={'mt-2 flex items-center gap-2 py-3'}
                    data-testid={'launched-header'}
                >
                    <Icon
                        name={'essential-checkmark'}
                        color={Colors.PRIMARY}
                        size={15}
                    />

                    <Text
                        weight={'bold'}
                        size={'small'}
                    >
                        {t('cycleEvaluationDashboard.managerLaunchStatus.launched', {
                            count: launchedTotalCount,
                        })}
                    </Text>
                </div>

                <div className={'border-base-200 overflow-hidden rounded-lg border'}>
                    <div
                        className={
                            'bg-base-50 border-base-200 grid grid-cols-[1fr_1fr_1fr] border-b px-4 py-3'
                        }
                        data-testid={'launched-table-header'}
                    >
                        <Text
                            weight={'bold'}
                            size={'small'}
                        >
                            {t('cycleEvaluationDashboard.managerLaunchStatus.columns.manager', {
                                managerLabel: getManagerLabelCapitalized(),
                            })}
                        </Text>

                        <Text
                            weight={'bold'}
                            size={'small'}
                            className={'text-center'}
                        >
                            {t(
                                'cycleEvaluationDashboard.managerLaunchStatus.columns.launchedRequired',
                            )}
                        </Text>

                        <Text
                            weight={'bold'}
                            size={'small'}
                            className={'text-right'}
                        >
                            {t('cycleEvaluationDashboard.managerLaunchStatus.columns.launchedOn')}
                        </Text>
                    </div>

                    <div className={'px-2 py-2'}>
                        <ManagerStatusSection
                            isLaunched={true}
                            isLoading={isLaunchedLoading}
                            managers={launchedManagers}
                            totalCount={launchedTotalCount}
                            onPageChange={setLaunchedPage}
                            currentPage={launchedPage}
                            totalPages={launchedTotalPages}
                        />
                    </div>
                </div>
            </div>
        </ModalDialog>
    );
};
